import { computed, ref } from 'vue'
import { useTheme } from 'vuetify'
import useEaster from '@/composables/themes/useEaster'
import useHalloween from '@/composables/themes/useHalloween'
import useChristmas from '@/composables/themes/useChristmas'
import defaultSVGPattern from '@/assets/patterns/defaultBackgroundSVGPattern'
import easterBackgroundSVGPattern from '@/assets/patterns/easterBackgroundSVGPattern'
import halloweenBackgroundSVGPattern from '@/assets/patterns/halloweenBackgroundSVGPattern'
import { useDisplayModeStore } from '@/store/displayMode'

const ALL_THEMES = {
  DEFAULT: 'default',
  EASTER: 'easter',
  HALLOWEEN: 'halloween',
  CHRISTMAS: 'christmas',
} as const
type CurrentTheme = (typeof ALL_THEMES)[keyof typeof ALL_THEMES]

function useBackgroundPattern(initialTheme?: CurrentTheme) {
  const theme = useTheme()
  const { isWithinEasterRange } = useEaster()
  const { isWithinHalloweenRange } = useHalloween()
  const { isWithinChristmasRange } = useChristmas()

  const currentTheme = ref<CurrentTheme>(initialTheme ?? ALL_THEMES.DEFAULT)

  const checkWhichPattern = () => {
    const displayMode = useDisplayModeStore().getDisplayMode()
    const color = theme.current.value.colors.onSurface

    const seasonalConfigs = [
      {
        condition: isWithinEasterRange.value || currentTheme.value === ALL_THEMES.EASTER,
        theme: ALL_THEMES.EASTER,
        pattern: easterBackgroundSVGPattern,
      },
      {
        condition: isWithinHalloweenRange.value || currentTheme.value === ALL_THEMES.HALLOWEEN,
        theme: ALL_THEMES.HALLOWEEN,
        pattern: halloweenBackgroundSVGPattern,
      },
      {
        condition: isWithinChristmasRange.value || currentTheme.value === ALL_THEMES.CHRISTMAS,
        theme: ALL_THEMES.CHRISTMAS,
        pattern: defaultSVGPattern,
      },
    ]

    if (displayMode?.useSeasonalStyling) {
      const active = seasonalConfigs.find(config => config.condition)
      if (active) {
        if (currentTheme.value !== active.theme) currentTheme.value = active.theme
        return `url("data:image/svg+xml,${encodeURIComponent(active.pattern(color))}")`
      }
    }

    if (!initialTheme) {
      currentTheme.value = ALL_THEMES.DEFAULT
    }
    return `url("data:image/svg+xml,${encodeURIComponent(defaultSVGPattern(color))}")`
  }

  const backgroundImage = computed(() => checkWhichPattern())

  return { currentTheme, backgroundImage }
}

export default useBackgroundPattern
